unit uLockerContracts;
(*
  PROMPT LOCKERS -- Core Contracts
  =================================
  UPDATED: 5/2/26
    Implements refactored code changes from last week:
    - TLockerBuffer replaced by TLockerRuntimeMgr
    - TAbstractUIBinding + TAbstractStorageBinding merged into TAbstractFieldBindings
    - TLockerContext added as session-layer cache
    - Agent pattern introduced: TLockerAgentBase, TOneStepAgent + stubs
    - TGetInputParams callback decouples param collection from UI
    - RunPrompt now takes TJSONObject instead of TInputPanels
    - Full request/response saved to OutputBuffer for audit/replay
    - TLockerAgentBase.Create is no-arg; Init() wires up shared dependencies
    - OpenRouterError moved to uLockerController as standalone reusable function

  KEY CLASSES
  -----------
  TLockerContext
    Session-layer global cache. Accumulates field values and metadata
    across all stages of a run. Created at run start, discarded (or
    saved for debugging/replay) at run end. Think of it as the web
    session object for a Locker run.

  TAbstractFieldBindings
    Single base class replacing the old TAbstractUIBinding +
    TAbstractStorageBinding pair.
    UI binding is optional -- not all agents need UI input.
    Storage binding is always present.

  TLockerAgentBase
    Abstract base for all agent types. Receives a context, executes,
    writes results to its own output buffer.
    Constructor is no-arg; call Init() after Create to wire dependencies.

  TOneStepAgent
    Concrete single-shot agent. The only agent type currently active.
    Packs inputs from context, fires prompt, writes result + usage back.

  TLockerRuntimeMgr
    Replaces TLockerBuffer. Owns the context, spawns agents based on
    TRunMode, manages callbacks to the UI layer.

  CALLBACKS (bridge between logic and UI)
  ----------------------------------------
  OnInputsNeeded  -- fired when an agent needs user input before executing
  OnResultReady   -- fired when an agent has completed and results are available
  OnError         -- fired when an agent fails
  OnGetInputParams -- returns a TJSONObject of params; source can be UI, metaprompt, batch, etc.

  EXECUTION FLOW
  --------------
  Entry points:
    RunLockerAsGuest  -- public-facing runner, no editor
    RunLocker_btnClick -- editor test runner

  Flow:
    Entry → TLockerRuntimeMgr.Start
          → loads locker definition into context (storage binding)
          → fires OnInputsNeeded (UI builds input form)
          → user hits Execute → TLockerRuntimeMgr.Execute
          → spawns TOneStepAgent (or other based on RunMode)
          → agent.Execute → calls OnGetInputParams, calls RunPrompt, writes output
          → fires OnResultReady (UI displays results)
          → optionally writes usage/snapshot to storage

  RUN MODES (TRunMode)
  --------------------
  rmOneStep      -- single input set, single result (currently active)
  rmAllowRepeats -- same inputs, run N times, accumulate results
  rmSerial       -- chain of stages, context grows at each stage,
                    may fire OnInputsNeeded between stages for more input
  rmBatch        -- no UI binding, iterates a list against same prompt
  rmFanOut       -- stage 1 generates list, parallel agents process each item,
                    TMergeAgent assembles outputs (not yet implemented)

  AGENT TYPES (planned)
  ---------------------
  TOneStepAgent   -- active
  TRepeatAgent    -- stub
  TSerialAgent    -- stub
  TBatchAgent     -- stub
  TFanOutAgent    -- stub
  TMergeAgent     -- stub
*)

interface

uses
  System.SysUtils, System.Classes, JS, Web, WEBLib.JSON, uLockerState, uStrLists;

type

  TAbstractFieldBindings = class;
  TLockerAgentBase       = class;
  TLockerRuntimeMgr      = class;

  TStringArray = array of string;

  // ── Agent status ──────────────────────────────────────────────────────────

  TAgentStatus = (asIdle, asRunning, asComplete, asError);

  // ── Callbacks ─────────────────────────────────────────────────────────────
  // These bridge the logic layer and the UI layer.
  // Wire them up in the form when there's a UI; leave nil for headless runs.

  TGetInputParams = reference to function: TJSONObject;
  // Returns a packed params object ready to pass to RunPrompt.
  // For UI runs: collects from TInputPanels.
  // For metaprompt runs: parses prior stage output.
  // For batch/fanout: builds from list item.

  TOnInputsNeeded      = TProc<TLockerAgentBase>;  // agent needs user input
  TOnResultReady       = TProc<TLockerAgentBase>;  // agent has a result to show
  TOnAgentError        = TProc<TLockerAgentBase, string>;  // agent failed, msg = why
  TOnAgentStatusChange = TProc<TLockerAgentBase, TAgentStatus>;  // agent status changed

  // ── TLockerContext ────────────────────────────────────────────────────────
  // Session-layer cache. Accumulates field values across all stages.
  // Agents read from it; they write only to their own output buffer.
  // The RuntimeMgr merges completed output buffers back into the context
  // before firing dependent agents.
  //
  // Also carries session metadata useful for logging and replay:
  //   SessionID, StartTime, UserID, LockerID
  //
  // Example -- reading a value:
  //   val := Context.Values['email'];
  //
  // Example -- merging an agent's output back in:
  //   Context.MergeFrom(Agent.OutputBuffer);

  THistoryRole = (hrUser, hrAssistant);

  TLockerContext = class
  private
    FSessionID  : string;
    FStartTime  : TDateTime;
    FUserID     : string;
    FLockerID   : string;
    FValues     : TStringlist;  // structured field data -- prompt token substitution
    FHistory    : TStringlist;  // conversation turns -- serialized JSON message objects
                                // passed to AI API as the 'messages' array in Serial runs

  public
    constructor Create(const aUserID, aLockerID: string);

    // ── Field values ─────────────────────────────────────────────────────
    function  GetValue(const aKey: string): string;
    procedure SetValue(const aKey, aValue: string);

    // Merge structured metadata from an agent's output into field values
    // NOTE: raw result text should NOT be merged here -- it goes to UI/storage
    // Only merge fields that downstream stages need as inputs
    procedure MergeFrom(aBuffer: TStringlist);

    // ── Conversation history ──────────────────────────────────────────────
    // Append some content to the history (call after each stage completes)
    procedure AppendToHistory(aRole: THistoryRole; const aContent: string);

    // Returns the full history as a JSON array string ready for the AI API
    // eg. [{"role":"user","content":"..."},{"role":"assistant","content":"..."}]
    function  HistoryToJSON: string;

    // Clear history -- eg. when starting a fresh Serial chain
    procedure ClearHistory;

    // ── Serialization ─────────────────────────────────────────────────────
    // Serialize entire context for storage (debugging / replay)
    function  ToJSON: string;

    property SessionID : string      read FSessionID;
    property StartTime : TDateTime   read FStartTime;
    property UserID    : string      read FUserID;
    property LockerID  : string      read FLockerID;
    property Values    : TStringlist read FValues;
    property History   : TStringlist read FHistory;
  end;

  // ── TAbstractFieldBindings ────────────────────────────────────────────────
  // Single base class replacing the old TAbstractUIBinding +
  // TAbstractStorageBinding pair.
  //
  // Each concrete binding owns its own buffer (TStringlist).
  // The buffer is internal -- callers use the pump methods, not the buffer directly.
  //
  // UI methods are optional -- override them only when there's a UI.
  // Storage methods are always required.

  TAbstractFieldBindings = class abstract
  protected
    FBuffer : TStringlist;  // owned by this binding

  public
    constructor Create;

    // ── UI <--> Buffer ───────────────────────────────────────────────────
    // Override these when there's a UI. Default impl does nothing.
    procedure LoadWidgetsFromBuffer; virtual;
    procedure SendWidgetsToBuffer;   virtual;

    // ── Buffer <--> Storage ──────────────────────────────────────────────
    // Always required -- override in concrete class.
    [async] function  LoadBufferFromStorage(const aLockerID: string): string; async; virtual; abstract;
    [async] function  SendBufferToStorage(const aLockerID: string): string; async;   virtual; abstract;

    // ── Buffer <--> Context ──────────────────────────────────────────────
    // Copy buffer contents into the shared context, or pull from it.
    procedure PushToContext(aContext: TLockerContext);   virtual;
    procedure PullFromContext(aContext: TLockerContext); virtual;

    // JSON accessors -- storage binding sets these after loading
    function MetadataJSON : TJSONObject; virtual;
    function PromptJSON   : TJSONObject; virtual;

    property Buffer : TStringlist read FBuffer;
  end;

  // ── TLockerAgentBase ──────────────────────────────────────────────────────
  // Abstract base for all agent types.
  // Each agent has:
  //   - A shared read context (TLockerContext) -- inputs, prior stage outputs
  //   - Its own output buffer -- results go here, not into the context directly
  //   - A status
  //   - An optional name for graph tracking
  //
  // Constructor is no-arg -- each subclass sets its own FName.
  // Call Init() after Create to wire up shared runtime dependencies.
  //
  // Example -- implementing a concrete agent:
  //   TMyAgent = class(TLockerAgentBase)
  //     constructor Create;
  //     [async] procedure Execute; override;
  //   end;
  //   constructor TMyAgent.Create;
  //   begin inherited; FName := 'MyAgent'; end;

  TLockerAgentBase = class abstract
  protected
    FName             : string;
    FStatus           : TAgentStatus;
    FContext          : TLockerContext;       // shared read-only input
    FOutputBuffer     : TStringlist;          // this agent's results
    FLockerID         : string;
    FBindings         : TAbstractFieldBindings;
    FController       : TObject;             // TLockerController -- TObject avoids circular ref
    FOnGetInputParams : TGetInputParams;

    FOnStatusChange   : TOnAgentStatusChange;

    // Use SetStatus instead of assigning FStatus directly --
    // fires OnStatusChange so the Dashboard can update its list
    procedure SetStatus(aStatus: TAgentStatus);

  public
    constructor Create;

    procedure Init(aContext          : TLockerContext;
                   aBindings         : TAbstractFieldBindings;
                   aController       : TObject;
                   aOnGetInputParams : TGetInputParams);

    [async] procedure Execute; async; virtual; abstract;

    property Name             : string                read FName;
    property Status           : TAgentStatus          read FStatus;
    property OutputBuffer     : TStringlist           read FOutputBuffer;
    property LockerID         : string                read FLockerID write FLockerID;
    property Controller       : TObject               read FController       write FController;
    property OnGetInputParams : TGetInputParams       read FOnGetInputParams write FOnGetInputParams;
    property OnStatusChange   : TOnAgentStatusChange  read FOnStatusChange   write FOnStatusChange;
  end;

  // ── TOneStepAgent ─────────────────────────────────────────────────────────
  // Single-shot agent. Currently the only active agent type.
  // Packs inputs from context, fires prompt, writes result + usage to OutputBuffer.
  // No UI knowledge -- by the time Execute is called, inputs are in the context.

  TOneStepAgent = class(TLockerAgentBase)
  public
    constructor Create;
    [async] procedure Execute; async; override;
  end;

  // ── Stub agents ───────────────────────────────────────────────────────────
  // Placeholders -- not yet implemented.

  TRepeatAgent  = class(TLockerAgentBase)
  public
    constructor Create;
    [async] procedure Execute; async; override;
  end;

  TSerialAgent  = class(TLockerAgentBase)
  public
    constructor Create;
    [async] procedure Execute; async; override;
  end;

  TBatchAgent   = class(TLockerAgentBase)
  public
    constructor Create;
    [async] procedure Execute; async; override;
  end;

  TFanOutAgent  = class(TLockerAgentBase)
  public
    constructor Create;
    [async] procedure Execute; async; override;
  end;

  TMergeAgent   = class(TLockerAgentBase)
  public
    constructor Create;
    [async] procedure Execute; async; override;
  end;

  // ── TLockerRuntimeMgr ─────────────────────────────────────────────────────
  // Replaces TLockerBuffer.
  // Owns the context, spawns agents based on RunMode, manages callbacks.
  //
  // Entry points:
  //   Start   -- load locker definition, set up context, fire OnInputsNeeded
  //   Execute -- user is done entering inputs, spawn agent and run it
  //
  // Callbacks -- wire these up in the form for UI runs; leave nil for headless:
  //   OnInputsNeeded  -- form should build input fields
  //   OnResultReady   -- form should display results
  //   OnError         -- form should show error
  //   OnGetInputParams -- form should return packed params TJSONObject
  //
  // Example -- wiring up in the form:
  //   FRuntimeMgr.OnInputsNeeded := procedure(a: TLockerAgentBase)
  //     begin SetupLockerUI(...) end;
  //   FRuntimeMgr.OnResultReady := procedure(a: TLockerAgentBase)
  //     begin DisplayResult(a.OutputBuffer) end;
  //   FRuntimeMgr.OnGetInputParams := function: TJSONObject
  //     begin ... collect from panels ... end;

  TLockerRuntimeMgr = class
  private
    FLockerState        : TLockerState;
    FBindings           : TAbstractFieldBindings;
    FContext            : TLockerContext;
    FCurrentAgent       : TLockerAgentBase;
    FController         : TObject;
    FOnGetInputParams   : TGetInputParams;
    FOnInputsNeeded     : TOnInputsNeeded;
    FOnResultReady      : TOnResultReady;
    FOnError            : TOnAgentError;
    FOnAgentStatusChange: TOnAgentStatusChange;

    function SpawnAgent : TLockerAgentBase;

  public
    constructor Create(aLockerState : TLockerState;
                       aBindings    : TAbstractFieldBindings;
                       aController  : TObject);

    // Entry point 1 -- called by RunLockerAsGuest / RunLocker_btnClick
    // Loads locker definition into context, fires OnInputsNeeded
    [async] function Start : string; async;  // '' = ok, else error

    // Entry point 2 -- called when user hits Execute (RunnerExecutePrompt_btnClick)
    // Spawns the appropriate agent and runs it
    [async] function Execute : string; async;  // '' = ok, else error

    property Context             : TLockerContext    read FContext;
    property CurrentAgent        : TLockerAgentBase  read FCurrentAgent;
    property Controller          : TObject              read FController          write FController;
    property OnGetInputParams    : TGetInputParams      read FOnGetInputParams    write FOnGetInputParams;
    property OnInputsNeeded      : TOnInputsNeeded      read FOnInputsNeeded      write FOnInputsNeeded;
    property OnResultReady       : TOnResultReady       read FOnResultReady       write FOnResultReady;
    property OnError             : TOnAgentError        read FOnError             write FOnError;
    property OnAgentStatusChange : TOnAgentStatusChange read FOnAgentStatusChange write FOnAgentStatusChange;
  end;

  // ── Utility functions ─────────────────────────────────────────────────────

  function ExternalName(const aCompoundKey: string): string;
  function InternalName(const aLabel: string): string;

var
  LockerFieldNames : TStringList;

implementation

uses uLockerController;

// ── TLockerContext ────────────────────────────────────────────────────────────

constructor TLockerContext.Create(const aUserID, aLockerID: string);
begin
  FSessionID := TGUID.NewGuid.ToString;
  FStartTime := Now;
  FUserID    := aUserID;
  FLockerID  := aLockerID;
  FValues    := TStringlist.Create;
  FHistory   := TStringlist.Create;
end;

function TLockerContext.GetValue(const aKey: string): string;
begin
  Result := FValues.Values[aKey];
end;

procedure TLockerContext.SetValue(const aKey, aValue: string);
begin
  FValues.Values[aKey] := aValue;
end;

procedure TLockerContext.MergeFrom(aBuffer: TStringlist);
var i : integer;
begin
  for i := 0 to aBuffer.Count-1 do
    FValues.Values[aBuffer.Names[i]] := aBuffer.ValueFromIndex[i];
end;

procedure TLockerContext.AppendToHistory(aRole: THistoryRole; const aContent: string);
var
  role : string;
  turn : TJSONObject;
begin
  case aRole of
    hrUser      : role := 'user';
    hrAssistant : role := 'assistant';
  end;
  turn := TJSONObject.Create;
  turn.AddPair('role',    role);
  turn.AddPair('content', aContent);
  FHistory.Add(turn.ToJSON);
end;

function TLockerContext.HistoryToJSON: string;
var i : integer;
begin
  Result := '[';
  for i := 0 to FHistory.Count-1 do
  begin
    if (i > 0) then Result := Result + ',';
    Result := Result + FHistory[i];
  end;
  Result := Result + ']';
end;

procedure TLockerContext.ClearHistory;
begin
  FHistory.Clear;
end;

function TLockerContext.ToJSON: string;
var
  obj : TJSONObject;
  i   : integer;
begin
  obj := TJSONObject.Create;
  obj.AddPair('session_id', FSessionID);
  obj.AddPair('locker_id',  FLockerID);
  obj.AddPair('user_id',    FUserID);
  obj.AddPair('history',    TJSONObject.ParseJSONValue(HistoryToJSON));
  for i := 0 to FValues.Count-1 do
    obj.AddPair(FValues.Names[i], FValues.ValueFromIndex[i]);
  Result := obj.ToJSON;
end;

// ── TAbstractFieldBindings ────────────────────────────────────────────────────

constructor TAbstractFieldBindings.Create;
begin
  FBuffer := TStringlist.Create;
end;

procedure TAbstractFieldBindings.LoadWidgetsFromBuffer;
begin
  // default: no-op -- override when there's a UI
end;

procedure TAbstractFieldBindings.SendWidgetsToBuffer;
begin
  // default: no-op -- override when there's a UI
end;

procedure TAbstractFieldBindings.PushToContext(aContext: TLockerContext);
var i : integer;
begin
  for i := 0 to FBuffer.Count-1 do
    aContext.SetValue(FBuffer.Names[i], FBuffer.ValueFromIndex[i]);
end;

procedure TAbstractFieldBindings.PullFromContext(aContext: TLockerContext);
var i : integer;
begin
  for i := 0 to aContext.Values.Count-1 do
    FBuffer.Values[aContext.Values.Names[i]] := aContext.Values.ValueFromIndex[i];
end;

function TAbstractFieldBindings.MetadataJSON: TJSONObject;
begin
  Result := nil;
end;

function TAbstractFieldBindings.PromptJSON: TJSONObject;
begin
  Result := nil;
end;

// ── TLockerAgentBase ──────────────────────────────────────────────────────────

constructor TLockerAgentBase.Create;
begin
  FStatus       := asIdle;
  FOutputBuffer := TStringlist.Create;
end;

procedure TLockerAgentBase.SetStatus(aStatus: TAgentStatus);
begin
  FStatus := aStatus;
  if Assigned(FOnStatusChange) then
    FOnStatusChange(Self, FStatus);
end;

procedure TLockerAgentBase.Init(aContext          : TLockerContext;
                                aBindings         : TAbstractFieldBindings;
                                aController       : TObject;
                                aOnGetInputParams : TGetInputParams);
begin
  FContext          := aContext;
  FBindings         := aBindings;
  FController       := aController;
  FOnGetInputParams := aOnGetInputParams;
end;

// ── Agent constructors ────────────────────────────────────────────────────────

constructor TOneStepAgent.Create;  begin inherited; FName := 'OneStep'; end;
constructor TRepeatAgent.Create;   begin inherited; FName := 'Repeat';  end;
constructor TSerialAgent.Create;   begin inherited; FName := 'Serial';  end;
constructor TBatchAgent.Create;    begin inherited; FName := 'Batch';   end;
constructor TFanOutAgent.Create;   begin inherited; FName := 'FanOut';  end;
constructor TMergeAgent.Create;    begin inherited; FName := 'Merge';   end;

// ── TOneStepAgent ─────────────────────────────────────────────────────────────

procedure TOneStepAgent.Execute;
var
  ctrl        : TLockerController;
  ParamsObj   : TJSONObject;
  Payload     : TJSONObject;
  ResponseObj : TJSONObject;
  ResultValue : TJSONValue;
  ErrorValue  : TJSONValue;
  UsageValue  : TJSONValue;
  t0, t1      : TDateTime;
  elapsed     : TTime;
  hr, mn, sc, ms : Word;
begin
  FStatus := asRunning;
  try
    // 1. build params from whatever source is wired up
    if Assigned(FOnGetInputParams) then
      ParamsObj := FOnGetInputParams()
    else
      ParamsObj := TJSONObject.Create;

    // 2. fire the prompt
    ctrl := TLockerController(FController);
    t0   := Now;
    ResponseObj := await(TJSONObject, ctrl.RunPrompt_async(FLockerID, ParamsObj));
    t1   := Now;

    // 3. save the full request and response for audit/replay
    Payload := TJSONObject.Create;
    Payload.AddPair('lockerID',   FLockerID);
    Payload.AddPair('parameters', ParamsObj);
    FOutputBuffer.Values['request']  := Payload.ToJSON;
    FOutputBuffer.Values['response'] := ResponseObj.ToJSON;

    // 4. unpack result
    ResultValue := ResponseObj.GetValue('result');
    ErrorValue  := ResponseObj.GetValue('error');

    if Assigned(ResultValue) then
    begin
      FOutputBuffer.Values['result'] := ResultValue.Value;

      elapsed := t1 - t0;
      DecodeTime(elapsed, hr, mn, sc, ms);
      FOutputBuffer.Values['elapsed'] := Format('%d:%d.%d', [mn, sc, ms]);

      UsageValue := ResponseObj.GetValue('usage');
      if Assigned(UsageValue) then
        FOutputBuffer.Values['usage'] := UsageValue.ToString;

      FStatus := asComplete;
    end
    else
    begin
      FOutputBuffer.Values['error'] := OpenRouterError(ResponseObj, ErrorValue, 'TOneStepAgent');
      FStatus := asError;
    end;

  except
    on E: Exception do
    begin
      FStatus := asError;
      FOutputBuffer.Values['error'] := E.Message;
    end;
  end;
end;

// ── Stub agents ───────────────────────────────────────────────────────────────

procedure TRepeatAgent.Execute;  begin SetStatus(asError); FOutputBuffer.Values['error'] := 'TRepeatAgent not yet implemented'; end;
procedure TSerialAgent.Execute;  begin SetStatus(asError); FOutputBuffer.Values['error'] := 'TSerialAgent not yet implemented';  end;
procedure TBatchAgent.Execute;   begin SetStatus(asError); FOutputBuffer.Values['error'] := 'TBatchAgent not yet implemented';   end;
procedure TFanOutAgent.Execute;  begin SetStatus(asError); FOutputBuffer.Values['error'] := 'TFanOutAgent not yet implemented';  end;
procedure TMergeAgent.Execute;   begin SetStatus(asError); FOutputBuffer.Values['error'] := 'TMergeAgent not yet implemented';   end;

// ── TLockerRuntimeMgr ─────────────────────────────────────────────────────────

constructor TLockerRuntimeMgr.Create(aLockerState : TLockerState;
                                     aBindings    : TAbstractFieldBindings;
                                     aController  : TObject);
begin
  FLockerState := aLockerState;
  FBindings    := aBindings;
  FController  := aController;
end;

function TLockerRuntimeMgr.SpawnAgent: TLockerAgentBase;
begin
  case FLockerState.RunMode of
    rmOneStep      : Result := TOneStepAgent.Create;
    rmAllowRepeats : Result := TRepeatAgent.Create;
    rmSerial       : Result := TSerialAgent.Create;
    rmBatch        : Result := TBatchAgent.Create;
    rmFanOut       : Result := TFanOutAgent.Create;
    else             Result := TOneStepAgent.Create;
  end;
  Result.Init(FContext, FBindings, FController, FOnGetInputParams);
  Result.OnStatusChange := FOnAgentStatusChange;
  FCurrentAgent := Result;
end;

function TLockerRuntimeMgr.Start: string;
begin
  Result := '';

  // create the session context
  FContext := TLockerContext.Create(FLockerState.UserID, FLockerState.LockerID);

  // load locker definition from storage into the binding's buffer
  Result := await(string, FBindings.LoadBufferFromStorage(FLockerState.LockerID));
  if (Result <> '') then Exit;

  // push loaded data into the shared context
  FBindings.PushToContext(FContext);

  // notify UI that inputs are needed (builds input form)
  if Assigned(FOnInputsNeeded) then
    FOnInputsNeeded(nil);  // no agent yet at this stage
end;

function TLockerRuntimeMgr.Execute: string;
var
  agent : TLockerAgentBase;
begin
  Result := '';

  // collect inputs from UI into the binding's buffer, then into context
  FBindings.SendWidgetsToBuffer;
  FBindings.PushToContext(FContext);

  // spawn and run the appropriate agent
  agent := SpawnAgent;
  agent.LockerID := FLockerState.LockerID;  // set before Execute

  await(agent.Execute);

  if (agent.Status = asError) then
  begin
    Result := agent.OutputBuffer.Values['error'];
    if Assigned(FOnError) then
      FOnError(agent, Result);
    Exit;
  end;

  // merge only structured metadata back into context (usage, status, timing)
  // raw result text stays in agent.OutputBuffer for the UI and storage
  FContext.SetValue('last_run_status', 'complete');
  FContext.SetValue('last_run_usage',  agent.OutputBuffer.Values['usage']);

  // notify UI that results are ready
  if Assigned(FOnResultReady) then
    FOnResultReady(agent);
end;

// ── Utility functions ─────────────────────────────────────────────────────────

function ExternalName(const aCompoundKey: string): string;
var parts : TStringArray;
begin
  parts := aCompoundKey.Split([':']);
  Result := parts[0];
end;

function InternalName(const aLabel: string): string;
begin
  Result := LockerFieldNames.Values[aLabel];
  if (Result = '') then
    Result := LowerCase(StringReplace(aLabel, ' ', '_', [rfReplaceAll]));
end;

initialization
  if not Assigned(LockerFieldNames) then
    LockerFieldNames := TStringList.Create;

end.
